import { FC, Suspense } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { ErrorRouteComponent, Link, Outlet } from '@tanstack/react-router'

import { Button, Cluster, Icon, Typography } from '@forto-dev/phoenix-ui-core'
import { Layout } from '@forto-dev/phoenix-ui-core/ship'
import { useLocalisation } from '@forto-dev/platform-localisation'

import { createErrorFallback } from '@/fragments/create-error-fallback'
import { Footer } from '@/fragments/footer'
import { Header } from '@/fragments/header'
import { Navigation } from '@/fragments/navigation'
import { WidgetSkeleton } from '@/fragments/widgetSkeleton'

import { reportsQueryOptions } from './api/reports.service'
import { ReportsSidebar } from './reportsSidebar'

const ReportTotal: FC = () => {
  const reports = useSuspenseQuery(reportsQueryOptions())
  const total = reports.data.length
  return <Typography color="muted">({total})</Typography>
}

export const ReportsLayout: FC = () => {
  const { translate } = useLocalisation()

  return (
    <Layout
      header={<Header />}
      navigation={<Navigation />}
      title={
        <Cluster gap={8} y="baseline" x="between">
          <Cluster gap={8} y="baseline">
            <Typography variant="h4">{translate('navigation.reports')}</Typography>
            <Suspense>
              <ReportTotal />
            </Suspense>
          </Cluster>
          <Button asChild size="lg" variant="outline-primary">
            <Link
              to="/reports/create"
              search={{
                type: 'container',
              }}
            >
              {/* Seems to be required to have some space between the icon and text */}
              <Cluster gap={8} y="center">
                <Icon name="Plus" />
                <Typography>{translate('reports.create_button')}</Typography>
              </Cluster>
            </Link>
          </Button>
        </Cluster>
      }
      sidebar={
        <Suspense fallback={<WidgetSkeleton count={4} />}>
          <ReportsSidebar />
        </Suspense>
      }
      main={<Outlet />}
      footer={<Footer />}
    />
  )
}

export const ReportsErrorComponent: ErrorRouteComponent = ({ error, reset }) => {
  const { translate } = useLocalisation()

  return (
    <Layout
      header={<Header />}
      navigation={<Navigation />}
      title={
        <Cluster gap={8} y="baseline" x="between">
          <Cluster gap={8} y="baseline">
            <Typography variant="h4">{translate('navigation.reports')}</Typography>
          </Cluster>
          <Button asChild size="lg" variant="outline-primary">
            <Link
              to="/reports/create"
              search={{
                type: 'container',
              }}
            >
              {/* Seems to be required to have some space between the icon and text */}
              <Cluster gap={8} y="center">
                <Icon name="Plus" />
                <Typography>{translate('reports.create_button')}</Typography>
              </Cluster>
            </Link>
          </Button>
        </Cluster>
      }
      main={createErrorFallback('An error occurred while loading reports')({ error, reset })}
      footer={<Footer />}
    />
  )
}
